import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Typography } from "@mui/material"
import { getLostFoundCaseStatus } from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { extractProperty, replaceRichTextLinks } from "utils"
import { commonSchema, regex } from "utils/FormSchema"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import * as yup from "yup"
import InformationBlocks from "./components/InformationBox"
import {
  CustomTextField,
  Header,
  NumbersController,
  prefixOptionsArray,
} from "./Forms/Common"
const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledForm = styled.div`
  display: flex;
  padding: 24px 16px;
  gap: 16px;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 24px;
    gap: 32px;
  }
`

const Strip = styled.div`
  display: flex;
  padding: 12px;
  align-items: center;
  gap: 12px;
  border-radius: 12px;
  background: rgba(27, 189, 119, 0.08);
  max-width: 700px;
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 32px;
    flex-direction: row;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 56px;
`

const StyledResponseWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: flex-start;
  border-top: 1px solid #d1d5db;
  padding-top: 32px;
  margin-top: 32px;
`

const StyledDot = styled.div<{ color?: string }>`
  min-width: 15px;
  min-height: 15px;
  border-radius: 200px;
  border: 3px solid white;
  background-color: ${(props) => props.color};
  box-shadow: 0px 0px 16px 0px ${(props) => props.color};
  margin-top: 5px;
`

const getStatusColor = (statusId: number): string => {
  switch (statusId) {
    case 0:
      return "#E00700"
    case 1:
      return "#F8A401"
    case 2:
      return "#1BBD77"
    case 3:
      return "#E00700"
    case 4:
      return "#1BBD77"
    case 5:
      return "#6F747D"
    case 6:
      return "#6F747D"
    default:
      return "#6B7280"
  }
}

export default function LossReports({
  data,
  locale,
}: {
  data: any
  locale: "he" | "en" | "ar" | "ru"
}) {
  const formTitle = extractProperty(data, "formTitle")
  const tipIcon = extractProperty(data, "tipIcon")
  const tipText = extractProperty(data, "tipText")
  const t = useTranslations("Forms")
  const tInner = useTranslations("InnerPages")
  const [response, setResponse] = useState<{
    statusId: number
    statusText: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const schema = yup.object({
    caseNumber: yup
      .string()
      .matches(regex.onlyNumbers, "מספר פנייה אינו תקין")
      .required("מספר פנייה אינו תקין"),
    cellphone: commonSchema.common(locale).phonePrefix,
    phone: commonSchema.common(locale).phone,
  })
  type FormData = yup.InferType<typeof schema>
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      cellphone: prefixOptionsArray[0].value,
    },
  })
  const onSubmit = async (data: FormData) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setError(null)
    setResponse(null)
    try {
      const res = await getLostFoundCaseStatus(
        Number(data.caseNumber),
        Number(data.cellphone + data.phone),
        locale,
      )
      if (res?.data?.errorCode) {
        setError(t("statusNotFound"))
        return
      }
      const body = res?.data?.body
      if (
        !body ||
        body.statusId == null ||
        body.statusId === 0 ||
        !body.statusText ||
        /^[A-Za-z]+$/.test(body.statusText)
      ) {
        setError(t("statusNotFound"))
        return
      }
      setResponse(body)
    } catch {
      setError(t("statusNotFound"))
    } finally {
      setIsSubmitting(false)
    }
  }
  return (
    <StyledLayout>
      <StyledForm>
        <Header title={formTitle} />
        <Strip>
          <Image
            src={getUmbracoMediaUrl(tipIcon)}
            alt=""
            width={32}
            height={32}
          />
          <Typography
            sx={{ "& p": { margin: 0 } }}
            variant="body2"
            dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(tipText) }}
          />
        </Strip>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <CustomTextField
            name="caseNumber"
            control={control}
            label="מספר פנייה"
            required={true}
            isSubmitted={isSubmitted}
            errors={errors}
          />
          <NumbersController
            phoneName="phone"
            prefixName="cellphone"
            control={control}
            label={t("mobileNumber")}
            required={true}
            isSubmitted={isSubmitted}
            errors={errors}
            locale={locale}
          />
          <StyledButton variant="primary" type="submit" disabled={isSubmitting}>
            {tInner("submitInquiry")}
          </StyledButton>
        </FormContainer>
        {error && (
          <StyledResponseWrapper>
            <StyledDot color="#E00700" />
            <Typography variant="body1" style={{ fontWeight: 700 }}>
              {error}
            </Typography>
          </StyledResponseWrapper>
        )}
        {response && (
          <StyledResponseWrapper>
            <StyledDot color={getStatusColor(response.statusId)} />
            <Typography variant="body1" style={{ fontWeight: 700 }}>
              {response.statusText}
            </Typography>
          </StyledResponseWrapper>
        )}
      </StyledForm>
      <InformationBlocks data={data} />
    </StyledLayout>
  )
}
